import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEditorStore } from '../../stores/editorStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useWordCount } from '../../hooks/useWordCount';

const StatusBar: React.FC = () => {
  const { activeDocument, lastSaved } = useEditorStore();
  const { showWordCount, showWordGoal, wordGoal } = useSettingsStore();
  const stats = useWordCount(activeDocument?.content || '');

  if (!showWordCount || !activeDocument) return null;

  const formatTime = (date: Date | null) => {
    if (!date) return 'Never';
    
    // Date 객체로 변환 (문자열인 경우를 대비)
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) return 'Never';
    
    const diffMinutes = Math.round((dateObj.getTime() - Date.now()) / 60000);
    
    if (Math.abs(diffMinutes) < 1) return 'Just now';
    
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
      diffMinutes,
      'minute'
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-0 left-0 right-0 glass-effect"
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center space-x-6">
              <span className="flex items-center space-x-2">
                <span className="font-medium">{stats.words}</span>
                <span>words</span>
                {showWordGoal && (
                  <span className="text-xs">
                    ({Math.round((stats.words / wordGoal) * 100)}% of {wordGoal})
                  </span>
                )}
              </span>
              <span className="flex items-center space-x-2">
                <span className="font-medium">{stats.characters}</span>
                <span>characters</span>
              </span>
              <span className="flex items-center space-x-2">
                <span className="font-medium">{stats.readingTime}</span>
                <span>min read</span>
              </span>
            </div>
            <div className="flex items-center space-x-4">
              {lastSaved && (
                <span className="text-xs">
                  Saved {formatTime(lastSaved)}
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StatusBar;